import { useEffect, useState, useCallback } from "react";
import LabChrome from "@/components/LabChrome";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ObjectContextMenu, ServerPropertiesDialog, DatabasePropertiesDialog, JobPropertiesDialog,
} from "@/components/sql/SqlAdmin";
import { ActivityMonitor, ErrorLogViewer, DbaScenarios } from "@/components/sql/SqlMonitor";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Database, Table2, ChevronRight, ChevronDown, Play, RotateCcw, Server,
  ListChecks, CheckCircle2, Circle, Loader2, FileCode2,
  Folder, Eye, Shield, Cog, Bell, Clock, ScrollText, KeyRound, Users, Zap,
  Boxes, FunctionSquare, GitBranch, Columns3, Activity, Braces, Network, HardDrive,
  ShieldAlert,
} from "lucide-react";

const ACCENT = "#8b5cf6";

function renderRich(text) {
  return String(text).split(/(\*\*.+?\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>);
}

export default function SqlLab() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "instructor";
  const [state, setState] = useState(null);
  const [activeDb, setActiveDb] = useState(null);
  const [objects, setObjects] = useState({}); // db -> {tables, views, programmability}
  const [admin, setAdmin] = useState(null);
  const [menu, setMenu] = useState(null); // {x,y,type,name,label}
  const [dialog, setDialog] = useState(null); // {kind, name, page}
  const [sql, setSql] = useState("SELECT * FROM Products;");
  const [readonly, setReadonly] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("results"); // results | exercises

  const load = useCallback(async () => {
    const { data } = await api.get("/sql/state");
    setState(data);
    if (!activeDb && data.databases?.length) setActiveDb(data.databases[0].name);
  }, [activeDb]);
  const loadAdmin = useCallback(async () => {
    const { data } = await api.get("/sql/admin");
    setAdmin(data);
  }, []);
  useEffect(() => { load(); loadAdmin(); }, []); // eslint-disable-line

  const openMenu = (e, type, name, label) => {
    e.preventDefault(); e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, type, name, label });
  };

  const jobObj = (name) => (admin?.agent?.jobs || []).find((j) => j.name === name);

  const onCtxAction = async (id, m) => {
    if (id === "refresh") {
      await loadAdmin();
      if (m.type === "database") { setObjects((o) => ({ ...o, [m.name]: undefined })); loadObjects(m.name); }
      toast.success("Refreshed."); return;
    }
    if (id === "newquery") { if (m.type === "database") setActiveDb(m.name); setTab("results"); toast("New Query", { description: `Query editor targeting ${m.name}` }); return; }
    if (id === "script" && m.type === "database") {
      const p = admin?.databases?.[m.name];
      setActiveDb(m.name);
      setSql(`-- Script generated from current metadata\nCREATE DATABASE [${m.name}]\n  CONTAINMENT = NONE;\nALTER DATABASE [${m.name}] SET RECOVERY ${p?.recovery_model || "FULL"};\nALTER DATABASE [${m.name}] SET COMPATIBILITY_LEVEL = ${(p?.compatibility_level || "160").split(" ")[0]};\nALTER DATABASE [${m.name}] SET PAGE_VERIFY ${p?.page_verify || "CHECKSUM"};`);
      setTab("results"); toast.success("Scripted to query editor."); return;
    }
    if (id === "properties") { setDialog({ kind: m.type, name: m.name }); return; }
    if (id === "history") { setDialog({ kind: "job", name: m.name, page: "history" }); return; }
    if (id === "agent-start" || id === "agent-stop") {
      const { data } = await api.post("/sql/admin/agent", { running: id === "agent-start" });
      setAdmin((a) => ({ ...a, agent: data })); toast.success(`SQL Server Agent ${data.status}.`); return;
    }
    if (["run", "enable", "disable"].includes(id)) {
      try {
        const { data } = await api.post(`/sql/admin/jobs/${encodeURIComponent(m.name)}/action`, { action: id });
        setAdmin((a) => ({ ...a, agent: { ...a.agent, jobs: a.agent.jobs.map((j) => j.name === m.name ? data : j) } }));
        toast.success(id === "run" ? `Job '${m.name}' started.` : `Job ${id}d.`);
        if (id === "run") setDialog({ kind: "job", name: m.name, page: "history" });
      } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    }
  };

  const applyAdminDb = (dbProps) => setAdmin((a) => ({ ...a, databases: { ...a.databases, [dbProps.name]: dbProps } }));
  const applyAdminServer = (srv) => setAdmin((a) => ({ ...a, server: srv }));
  const applyAdminJob = (job) => setAdmin((a) => ({ ...a, agent: { ...a.agent, jobs: a.agent.jobs.map((j) => j.name === job.name ? job : j) } }));

  const loadObjects = async (name) => {
    if (objects[name]) return;
    try {
      const { data } = await api.get(`/sql/databases/${name}/objects`);
      setObjects((o) => ({ ...o, [name]: data }));
    } catch { setObjects((o) => ({ ...o, [name]: { tables: [], views: [], programmability: {} } })); }
  };

  const selectObject = (dbName, kind, obj) => {
    setActiveDb(dbName);
    if (kind === "table" || kind === "view") setSql(`SELECT * FROM ${obj} LIMIT 100;`);
    else if (kind === "proc") setSql(`-- Stored procedure (training object)\nEXEC ${obj};`);
    else if (kind === "function") setSql(`-- Scalar function (training object)\nSELECT ${obj}(/* args */);`);
    else if (kind === "trigger") setSql(`-- Trigger: ${obj} (fires automatically on DML)`);
    setTab("results");
  };

  const onJob = (j) => {
    toast(j.name, { description: `${j.enabled ? "Enabled" : "Disabled"} · ${j.schedule} · Last run: ${j.last_run} · Category: ${j.category}` });
  };

  const runQuery = async () => {
    if (!activeDb) { toast.error("Select a database first."); return; }
    setBusy(true); setTab("results");
    try {
      const { data } = await api.post("/sql/query", { database: activeDb, sql, readonly });
      setResult(data);
      if (data.error) toast.error(data.message || "Query error");
    } catch (e) { toast.error("Query failed."); }
    finally { setBusy(false); }
  };

  // Drop an exact query from a guided step into the editor and run it.
  const runSql = async (db, text) => {
    setActiveDb(db);
    setSql(text);
    setTab("results");
    setBusy(true);
    try {
      const { data } = await api.post("/sql/query", { database: db, sql: text, readonly });
      setResult(data);
      if (data.error) toast.error(data.message || "Query error");
      else toast.success("Query executed — result loaded below.");
    } catch (e) { toast.error("Query failed."); }
    finally { setBusy(false); }
  };

  const resetDb = async () => {
    if (!activeDb) return;
    await api.post(`/sql/databases/${activeDb}/reset`);
    const { data } = await api.get(`/sql/databases/${activeDb}/objects`);
    setObjects((o) => ({ ...o, [activeDb]: data }));
    toast.success(`${activeDb} reset to seed data.`);
  };

  const resetLab = async () => {
    if (!window.confirm("Instructor Reset: this restores server/database properties, jobs, error log and clears all active incident scenarios for your account. Continue?")) return;
    try {
      await api.post("/sql/admin/reset");
      await loadAdmin();
      toast.success("SQL lab administration state reset to baseline.");
    } catch { toast.error("Reset failed."); }
  };

  const validateTask = async (task) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sql/tasks/${task.id}/validate`, { sql });
      if (data.passed) { toast.success(data.message); await load(); }
      else toast.error(data.message);
    } finally { setBusy(false); }
  };

  return (
    <LabChrome title="SQL Database Lab" subtitle={state?.server ? `Connected to ${state.server}` : "Connecting…"} accent={ACCENT} contentClassName="flex">
      {/* Object Explorer — SSMS-style tree */}
      <aside className="w-80 shrink-0 border-r border-border bg-card overflow-y-auto text-foreground" data-testid="sql-object-explorer">
        <div className="px-3 py-2 border-b border-border flex items-center gap-2 text-xs font-semibold sticky top-0 bg-card z-10">
          <Server className="h-4 w-4" style={{ color: ACCENT }} /> Object Explorer
        </div>
        {state?.tree ? (
          <div className="py-1">
            <Node depth={0} icon={Server} color="#10b981" label={state.tree.instance?.display || state.server} defaultOpen testid="tree-server"
              onContext={(e) => openMenu(e, "server", "HMG-SQL01", state.tree.instance?.display)}>
              {/* Databases */}
              <Node depth={1} icon={Folder} color="#eab308" label="Databases" defaultOpen testid="tree-databases">
                <Node depth={2} icon={Folder} color="#eab308" label="System Databases" testid="tree-system-databases">
                  {(state.tree.system_databases || []).map((n) => (
                    <DbNode key={n} depth={3} name={n} system objects={objects} onOpen={() => loadObjects(n)}
                      onSelectObj={selectObject} />
                  ))}
                </Node>
                <Node depth={2} icon={Folder} color="#eab308" label="Database Snapshots" testid="tree-db-snapshots" />
                {(state.databases || []).map((d) => (
                  <DbNode key={d.name} depth={2} name={d.name} tables={d.tables} objects={objects}
                    onOpen={() => { setActiveDb(d.name); loadObjects(d.name); }} onSelectObj={selectObject}
                    onContext={(e) => openMenu(e, "database", d.name)} testid={`tree-db-${d.name}`} />
                ))}
              </Node>

              {/* Server-level folders */}
              <Node depth={1} icon={Shield} color="#f87171" label="Security" testid="tree-security">
                <Node depth={2} icon={Folder} color="#eab308" label="Logins" testid="tree-logins">
                  {(state.tree.security?.logins || []).map((l) => <Leaf key={l} depth={3} icon={KeyRound} color="#f59e0b" label={l} />)}
                </Node>
                <Node depth={2} icon={Folder} color="#eab308" label="Server Roles">
                  {(state.tree.security?.server_roles || []).map((r) => <Leaf key={r} depth={3} icon={Users} color="#60a5fa" label={r} />)}
                </Node>
                <Node depth={2} icon={Folder} color="#eab308" label="Credentials">
                  {(state.tree.security?.credentials || []).map((c) => <Leaf key={c} depth={3} icon={KeyRound} color="#f59e0b" label={c} />)}
                </Node>
              </Node>

              <Node depth={1} icon={Boxes} color="#a78bfa" label="Server Objects" testid="tree-server-objects">
                {(state.tree.server_objects || []).map((s) => <Node key={s} depth={2} icon={Folder} color="#eab308" label={s} />)}
              </Node>

              <Node depth={1} icon={GitBranch} color="#34d399" label="Replication" testid="tree-replication">
                {(state.tree.replication || []).map((s) => <Node key={s} depth={2} icon={Folder} color="#eab308" label={s} />)}
              </Node>

              <Node depth={1} icon={Network} color="#22d3ee" label="Always On High Availability" testid="tree-alwayson">
                <Node depth={2} icon={Folder} color="#eab308" label="Availability Groups" />
              </Node>

              <Node depth={1} icon={Cog} color="#94a3b8" label="Management" defaultOpen testid="tree-management">
                <Leaf depth={2} icon={Activity} color={ACCENT} label="Activity Monitor" testid="tree-activity-monitor" onClick={() => setTab("monitor")} />
                {(state.tree.management || []).map((m) => (
                  <Leaf key={m} depth={2} icon={m === "SQL Server Logs" ? ScrollText : Cog} color="#94a3b8" label={m}
                    testid={m === "SQL Server Logs" ? "tree-sql-server-logs" : undefined}
                    onClick={m === "SQL Server Logs" ? () => setTab("errorlog") : undefined} />
                ))}
              </Node>

              <Node depth={1} icon={Boxes} color="#a78bfa" label="Integration Services Catalogs" testid="tree-ssis">
                <Node depth={2} icon={Folder} color="#eab308" label="SSISDB" />
              </Node>

              {/* SQL Server Agent + job folders */}
              <Node depth={1} icon={Activity} color="#f472b6" label="SQL Server Agent" defaultOpen testid="tree-agent"
                onContext={(e) => openMenu(e, "agent", "SQL Server Agent")}>
                <Node depth={2} icon={Folder} color="#eab308" label="Jobs" defaultOpen testid="tree-agent-jobs">
                  {(state.tree.agent?.job_folders || []).map((jf) => (
                    <Node key={jf.folder} depth={3} icon={Folder} color="#eab308" label={jf.folder} badge={jf.jobs.length} testid={`tree-jobfolder-${jf.folder}`}>
                      {jf.jobs.map((j) => (
                        <Leaf key={j.name} depth={4} icon={Clock} color={j.enabled ? "#34d399" : "#94a3b8"} label={j.name}
                          testid={`tree-job-${j.name}`}
                          onClick={() => onJob(j)}
                          onContext={(e) => openMenu(e, "job", j.name)} />
                      ))}
                    </Node>
                  ))}
                </Node>
                <Node depth={2} icon={Folder} color="#eab308" label="Job Activity Monitor" testid="tree-job-activity-monitor" onClick={() => setTab("monitor")} />
                <Node depth={2} icon={Bell} color="#fbbf24" label="Alerts" testid="tree-agent-alerts">
                  {(state.tree.agent?.alerts || []).map((a) => <Leaf key={a} depth={3} icon={Bell} color="#fbbf24" label={a} />)}
                </Node>
                <Node depth={2} icon={Users} color="#60a5fa" label="Operators">
                  {(state.tree.agent?.operators || []).map((o) => <Leaf key={o} depth={3} icon={Users} color="#60a5fa" label={o} />)}
                </Node>
                <Node depth={2} icon={Shield} color="#f87171" label="Proxies">
                  {(state.tree.agent?.proxies || []).map((p) => <Leaf key={p} depth={3} icon={Shield} color="#f87171" label={p} />)}
                </Node>
                <Node depth={2} icon={ScrollText} color="#94a3b8" label="Error Logs" testid="tree-agent-error-logs" onClick={() => setTab("errorlog")} />
              </Node>

              <Node depth={1} icon={Zap} color="#f59e0b" label="XEvent Profiler" testid="tree-xevent" />
            </Node>
          </div>
        ) : <div className="p-3 text-xs text-muted-foreground">Connecting…</div>}
      </aside>

      {/* Query area */}
      <section className="flex-1 min-w-0 flex flex-col">
        <div className="border-b border-border bg-card px-4 py-2 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-muted-foreground">Database:</span>
          <span className="text-sm font-medium">{activeDb || "—"}</span>
          <div className="h-5 w-px bg-border" />
          <Button size="sm" onClick={runQuery} disabled={busy} className="gap-1.5" data-testid="sql-run" style={{ background: ACCENT, color: "#fff" }}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Execute
          </Button>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Switch checked={readonly} onCheckedChange={setReadonly} data-testid="sql-readonly-toggle" />
            db_datareader (read-only)
          </div>
          <Button size="sm" variant="outline" onClick={resetDb} className="gap-1.5 ml-auto" data-testid="sql-reset"><RotateCcw className="h-3.5 w-3.5" /> Reset DB</Button>
          {isStaff && (
            <Button size="sm" variant="outline" onClick={resetLab} className="gap-1.5 border-rose-500/40 text-rose-500 hover:text-rose-500" data-testid="sql-instructor-reset">
              <ShieldAlert className="h-3.5 w-3.5" /> Instructor Reset
            </Button>
          )}
        </div>

        <textarea value={sql} onChange={(e) => setSql(e.target.value)} data-testid="sql-editor" spellCheck={false}
          className="h-40 shrink-0 w-full bg-[#0b1020] text-emerald-200 font-mono text-sm p-4 outline-none resize-none border-b border-border"
          placeholder="Write T-SQL / SQL here…" />

        {/* tabs */}
        <div className="border-b border-border bg-card px-4 flex items-center gap-1 overflow-x-auto">
          {[["results", "Results", ListChecks], ["exercises", "Guided Exercises", FileCode2],
            ["scenarios", "DBA Scenarios", ShieldAlert], ["monitor", "Activity Monitor", Activity],
            ["errorlog", "Error Log", ScrollText]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`sql-tab-${id}`}
              className={`px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${tab === id ? "border-[color:var(--acc)] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              style={{ ["--acc"]: ACCENT }}>
              {label}{id === "exercises" && state?.progress ? ` (${state.progress.done}/${state.progress.total})` : ""}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-auto">
          {tab === "results" && <ResultsGrid result={result} />}
          {tab === "exercises" && <Exercises state={state} onValidate={validateTask} setSql={setSql} setActiveDb={setActiveDb} onRunStep={runSql} busy={busy} />}
          {tab === "scenarios" && <DbaScenarios onStateChange={loadAdmin} />}
          {tab === "monitor" && <ActivityMonitor />}
          {tab === "errorlog" && <ErrorLogViewer />}
        </div>
      </section>

      <ObjectContextMenu menu={menu} onClose={() => setMenu(null)} onAction={onCtxAction} />
      {dialog?.kind === "server" && admin && (
        <ServerPropertiesDialog open onClose={() => setDialog(null)} server={admin.server} onSave={applyAdminServer} />
      )}
      {dialog?.kind === "database" && admin?.databases?.[dialog.name] && (
        <DatabasePropertiesDialog open onClose={() => setDialog(null)} dbProps={admin.databases[dialog.name]} onSave={applyAdminDb} />
      )}
      {dialog?.kind === "job" && jobObj(dialog.name) && (
        <JobPropertiesDialog open onClose={() => setDialog(null)} job={jobObj(dialog.name)} initialPage={dialog.page} onChanged={applyAdminJob} />
      )}
    </LabChrome>
  );
}

function ResultsGrid({ result }) {
  if (!result) return <div className="p-8 text-sm text-muted-foreground" data-testid="sql-results-empty">Run a query to see results.</div>;
  if (result.error) return <div className="p-4 m-4 rounded-md border border-rose-500/40 bg-rose-500/5 text-rose-500 text-sm font-mono" data-testid="sql-error">{result.message}</div>;
  const cols = result.columns || [];
  return (
    <div data-testid="sql-results">
      {cols.length > 0 ? (
        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted sticky top-0"><tr>{cols.map((c) => <th key={c} className="text-left font-semibold px-3 py-2 whitespace-nowrap border-b border-border">{c}</th>)}</tr></thead>
            <tbody>
              {(result.rows || []).map((r, i) => <tr key={i} className="border-b border-border/60 hover:bg-accent/50">{r.map((v, j) => <td key={j} className="px-3 py-1.5 whitespace-nowrap font-mono">{v === null ? <span className="text-muted-foreground italic">NULL</span> : String(v)}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      ) : null}
      <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border bg-card">{result.message}</div>
    </div>
  );
}

function extractQuery(step) {
  const m = String(step).match(/\*\*(.+?)\*\*/);
  if (!m) return null;
  const q = m[1].trim();
  if (/^\s*select\b/i.test(q) && /\bfrom\b/i.test(q)) return q.replace(/;+\s*$/, "") + ";";
  return null;
}

function Exercises({ state, onValidate, setSql, setActiveDb, onRunStep, busy }) {
  const tasks = state?.tasks || [];
  return (
    <div className="p-4 space-y-3" data-testid="sql-exercises">
      <div className="text-sm text-muted-foreground">Write a query that satisfies each exercise, run it, then click <strong>Check</strong>. Your result set is validated against the expected output.</div>
      {tasks.map((t, i) => (
        <div key={t.id} className="rounded-lg border border-border bg-card p-4" data-testid={`sql-exercise-${t.id}`}>
          <div className="flex items-start gap-3">
            {t.passed ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{i + 1}. {t.title}</span>
                <span className="text-[11px] px-2 py-0.5 rounded bg-secondary text-secondary-foreground">{t.database}</span>
                <span className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground">{t.skill}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{t.brief}</p>
              {(t.steps || []).length > 0 && (
                <div className="mt-2.5 rounded-md border border-[color:var(--acc)]/25 bg-[color:var(--acc)]/5 p-2.5" style={{ ["--acc"]: ACCENT }} data-testid={`sql-steps-${t.id}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold mb-1.5" style={{ color: ACCENT }}>
                    <ListChecks className="h-3.5 w-3.5" /> Steps to complete
                  </div>
                  <ol className="space-y-1.5">
                    {t.steps.map((s, si) => {
                      const q = extractQuery(s);
                      return (
                        <li key={si} className="flex gap-2 text-xs leading-relaxed items-start">
                          <span className="shrink-0 h-4 w-4 rounded-full text-white text-[10px] font-bold grid place-items-center mt-0.5" style={{ background: ACCENT }}>{si + 1}</span>
                          <span className="flex-1">{renderRich(s)}</span>
                          {q && (
                            <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[10px] shrink-0" disabled={busy}
                              data-testid={`sql-run-step-${t.id}-${si}`} onClick={() => onRunStep(t.database, q)} title="Drop this query into the editor and run it">
                              <Play className="h-3 w-3" /> Run
                            </Button>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" variant="outline" className="gap-1.5" data-testid={`sql-load-${t.id}`}
                  onClick={() => { setActiveDb(t.database); setSql(t.hint); }}>
                  Load starter into editor
                </Button>
                <Button size="sm" className="gap-1.5" disabled={busy} data-testid={`sql-check-${t.id}`} style={{ background: ACCENT, color: "#fff" }}
                  onClick={() => onValidate(t)}>Check</Button>
                {t.passed && <span className="text-xs text-emerald-500 font-medium">Completed</span>}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------- SSMS tree node primitives ----------------
function Row({ depth = 0, icon: Icon, color, label, badge, open, hasKids, onClick, onContext, testid }) {
  return (
    <button onClick={onClick} onContextMenu={onContext} data-testid={testid}
      className="w-full flex items-center gap-1 py-[3px] pr-2 text-xs hover:bg-accent text-left"
      style={{ paddingLeft: depth * 14 + 8 }}>
      {hasKids
        ? (open ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />)
        : <span className="w-3 shrink-0" />}
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" style={color ? { color } : {}} />}
      <span className="truncate">{label}</span>
      {badge != null && <span className="ml-auto text-[10px] text-muted-foreground">{badge}</span>}
    </button>
  );
}

function Node({ depth = 0, icon, color, label, badge, testid, defaultOpen = false, onOpen, onClick, onContext, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && onOpen) onOpen();
    if (onClick) onClick();
  };
  return (
    <>
      <Row depth={depth} icon={icon} color={color} label={label} badge={badge} open={open}
        hasKids={children != null} onClick={children != null ? toggle : onClick} onContext={onContext} testid={testid} />
      {open && children != null && <div>{children}</div>}
    </>
  );
}

function Leaf({ depth = 0, icon, color, label, onClick, onContext, testid }) {
  return <Row depth={depth} icon={icon} color={color} label={label} hasKids={false} onClick={onClick} onContext={onContext} testid={testid} />;
}

// A user/system database node with the full SSMS folder set.
function DbNode({ depth, name, tables, system, objects, onOpen, onSelectObj, onContext, testid }) {
  const obj = objects[name];
  const prog = obj?.programmability || {};
  return (
    <Node depth={depth} icon={Database} color={system ? "#94a3b8" : "#a78bfa"} label={name} badge={system ? undefined : tables}
      testid={testid} onOpen={onOpen} onContext={onContext}>
      <Node depth={depth + 1} icon={Folder} color="#eab308" label="Database Diagrams" />
      <Node depth={depth + 1} icon={Folder} color="#eab308" label="Tables" badge={obj?.tables?.length}>
        {system ? <Leaf depth={depth + 2} icon={Table2} color="#38bdf8" label="(system tables)" />
          : (obj?.tables || []).map((t) => (
            <Node key={t.name} depth={depth + 2} icon={Table2} color="#38bdf8" label={`dbo.${t.name}`} badge={t.rows}
              testid={`tree-table-${t.name}`} onClick={() => onSelectObj(name, "table", t.name)}>
              <Node depth={depth + 3} icon={Folder} color="#eab308" label="Columns">
                {(t.columns || []).map((c) => (
                  <Leaf key={c.name} depth={depth + 4} icon={Columns3} color={c.pk ? "#f59e0b" : "#94a3b8"}
                    label={`${c.name} (${c.type}${c.pk ? ", PK" : ""}${c.notnull && !c.pk ? ", not null" : ""})`} />
                ))}
              </Node>
              <Node depth={depth + 3} icon={Folder} color="#eab308" label="Keys" />
              <Node depth={depth + 3} icon={Folder} color="#eab308" label="Indexes" />
              <Node depth={depth + 3} icon={Folder} color="#eab308" label="Triggers" />
            </Node>
          ))}
      </Node>
      <Node depth={depth + 1} icon={Folder} color="#eab308" label="Views" badge={obj?.views?.length}>
        {(obj?.views || []).map((v) => (
          <Leaf key={v.name} depth={depth + 2} icon={Eye} color="#34d399" label={`dbo.${v.name}`}
            testid={`tree-view-${v.name}`} onClick={() => onSelectObj(name, "view", v.name)} />
        ))}
      </Node>
      <Node depth={depth + 1} icon={Folder} color="#eab308" label="External Resources" />
      <Node depth={depth + 1} icon={Folder} color="#eab308" label="Synonyms" />
      <Node depth={depth + 1} icon={Folder} color="#eab308" label="Programmability">
        <Node depth={depth + 2} icon={Folder} color="#eab308" label="Stored Procedures" badge={(prog.stored_procedures || []).length}>
          {(prog.stored_procedures || []).map((p) => (
            <Leaf key={p} depth={depth + 3} icon={FileCode2} color="#f472b6" label={`dbo.${p}`}
              testid={`tree-proc-${p}`} onClick={() => onSelectObj(name, "proc", p)} />
          ))}
        </Node>
        <Node depth={depth + 2} icon={Folder} color="#eab308" label="Functions" badge={(prog.functions || []).length}>
          {(prog.functions || []).map((f) => (
            <Leaf key={f} depth={depth + 3} icon={FunctionSquare} color="#22d3ee" label={`dbo.${f}`}
              onClick={() => onSelectObj(name, "function", f)} />
          ))}
        </Node>
        <Node depth={depth + 2} icon={Folder} color="#eab308" label="Database Triggers" badge={(prog.triggers || []).length}>
          {(prog.triggers || []).map((tr) => (
            <Leaf key={tr} depth={depth + 3} icon={Zap} color="#fbbf24" label={tr}
              onClick={() => onSelectObj(name, "trigger", tr)} />
          ))}
        </Node>
        <Node depth={depth + 2} icon={Folder} color="#eab308" label="Types" badge={(prog.types || []).length}>
          {(prog.types || []).map((ty) => <Leaf key={ty} depth={depth + 3} icon={Braces} color="#a78bfa" label={ty} />)}
        </Node>
      </Node>
      <Node depth={depth + 1} icon={Folder} color="#eab308" label="Query Store" />
      <Node depth={depth + 1} icon={Folder} color="#eab308" label="Service Broker" />
      <Node depth={depth + 1} icon={HardDrive} color="#94a3b8" label="Storage" />
      <Node depth={depth + 1} icon={Shield} color="#f87171" label="Security" />
    </Node>
  );
}
